import {
  ApiDeviceSite,
  ApiMeasurePoint,
  ApiReturnAllSensor,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasureType,
  ApiReturnSite,
} from '@highimpact-frontend/api-types';
import { forwardRef, useState } from 'react';

import { getMeasureTypesFromDeviceType } from '../../store/config';
import { resetExternalState } from '../../store/data/slice';
import { DataState } from '../../store/data/state';
import { useAppDispatch } from '../../store/hooks';
import {
  createValidatorWithAllValues,
  useForm,
} from '../../utils/hooks/useForm';

import { coordinates, Point } from '@highimpact-frontend/map-geometry';
import SaveIcon from '@mui/icons-material/Save';
import { Button, Stack } from '@mui/material';
import StationService from '../../api/services/config/measurePoint/MeasurePointService';
import { formatDateToLocalString } from '../../utils/date';
import { getEntityById } from '../../utils/functions';
import { useSubmit } from '../../utils/hooks/useSubmit';
import DateTimeRangePickerComponent from '../utils/MUISpecifics/DateTimeRangePicker';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import DropDownItemSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import TextInput from '../utils/MUISpecifics/inputText/TextInput';
import CoordianatesPicker from '../utils/coordiantesPicker/CoordianatesPicker';
import { useTranslation } from 'react-i18next';

interface CreateStationProps {
  onCancel: () => void;
  isOpen: boolean;
  deviceTypeMeasureTypes: DataState<
    string,
    { [key: number]: ApiReturnDeviceTypeMeasureType }
  >;
  device_types: ApiReturnDeviceType[];
  measureTypes: DataState<string, { [key: number]: ApiReturnMeasureType }>;
  allDevices: ApiReturnAllSensor[];
  freeDevices: ApiReturnAllSensor[];
  site: ApiReturnSite;
}

function CreateStation({
  onCancel,
  device_types,
  deviceTypeMeasureTypes,
  measureTypes,
  allDevices: devices,
  freeDevices,
  isOpen,
  site,
}: CreateStationProps): JSX.Element {
  const defaultValues: ApiMeasurePoint & {
    device_type_id: number | null;
  } & ApiDeviceSite = {
    name: '',
    site_id: site.id,
    geom_point: {
      type: 'POINT',
      coordinates: [
        site.position?.coordinates[0] ?? 0,
        site.position?.coordinates[1] ?? 0,
      ],
    },
    device_type_id: null,
    device_id: 0,
    device_site: {
      device_id: 0,
      site_id: site.id,
      indicators: {},
      start_assignement_date: new Date(0).toISOString(),
      end_assignement_date: null,
      measure_point_id: null,
    },
  };

  const { t } = useTranslation('component.stations');
  const dispatch = useAppDispatch();
  const { submit, state } = useSubmit();

  const [filtredProposition, setFiltredProposition] = useState<{
    deviceType: ApiReturnDeviceType | null;
    measureTypes: ApiReturnMeasureType[];
    devices: ApiReturnAllSensor[];
  }>({ deviceType: null, measureTypes: [], devices: [] });

  const trueDeviceTypes = device_types.filter((dt) =>
    freeDevices.some((d) => dt.id === d.device_type_id)
  );

  const getDeviceTypeById = (id: number) =>
    device_types.find((dt) => dt.id === id);
  const getDevicesByDeviceType = (deviceType: ApiReturnDeviceType) =>
    devices.filter((d) => d.device_type_id === deviceType.id);

  const updateMeasures = (id: number | null) => {
    if (id !== null) {
      const deviceType = getDeviceTypeById(id);
      if (!deviceType) return;
      const measures = getMeasureTypesFromDeviceType(
        deviceType,
        deviceTypeMeasureTypes,
        measureTypes
      );
      const relatedDevices = getDevicesByDeviceType(deviceType).filter((d) =>
        freeDevices.some((sd) => sd.id === d.id)
      );
      setFiltredProposition({
        deviceType,
        measureTypes: measures,
        devices: relatedDevices,
      });
    } else {
      setFiltredProposition({
        deviceType: null,
        measureTypes: [],
        devices: [],
      });
    }
  };

  const validator = createValidatorWithAllValues<
    ApiMeasurePoint & { device_type_id: number | null } & ApiDeviceSite
  >({
    site_id: [],
    geom_point: [
      (n) =>
        n === null || n === undefined
          ? t('Choisir une position sur la carte')
          : undefined,
    ],
    device_type_id: [
      (n) => (n === null ? t('Le champ est obligatoire') : undefined),
    ],
    name: [
      (n) =>
        !n || n.trim().length <= 1 ? t('Le champ est obligatoire') : undefined,
    ],
    device_id: [
      (n) =>
        n === undefined || n <= 0
          ? t('Sélectionner un dispositif existant')
          : undefined,
    ],
    start_assignement_date: [
      (n, values) => {
        const isAuto =
          values.device_type_id != null &&
          getDeviceTypeById(values.device_type_id)?.type === 'automatic_sensor';

        //if not automatic_sensor - the dates are not required
        if (!isAuto) return undefined;

        if (!n) return 'La date est requise';
        return isNaN(Date.parse(n)) ? 'La date doit être valide' : undefined;
      },
    ],
    end_assignement_date: [
      (n, values) => {
        const isAuto =
          values.device_type_id != null &&
          getDeviceTypeById(values.device_type_id)?.type === 'automatic_sensor';

        //not check data, if type is not 'automatic_sensor'
        if (!isAuto) return undefined;

        if (!n) return 'La date est requise';
        return isNaN(Date.parse(n)) ? 'La date doit être valide' : undefined;
      },
    ],
  });

  const onSubmit = async (
    data: ApiMeasurePoint & {
      device_configuration_id?: number | undefined | null;
    }
  ) => {
    submit({
      callbacks: [
        async () => await StationService.add(data),
        () => dispatch(resetExternalState({ external_key: 'devices' })),
        () => dispatch(resetExternalState({ external_key: 'site_devices' })),
        () => dispatch(resetExternalState({ external_key: 'measure_points' })),
        () => dispatch(resetExternalState({ external_key: 'sites' })),
        () => onCancel(),
      ],
      errorMessages: {
        anyError:
          'Une erreur est survenue lors de la création de la station de mesure.',
        unicityError: 'Une station de mesure du même nom existe déjà',
      },
      successMessage: 'La station a été créée avec succès',
    });
  };

  const methods = useForm(defaultValues, validator, onSubmit);

  /**
   * Retrieve coordinates.
   */
  const handleCoordinateSelect = (value: { lat: number; lng: number }) => {
    // Créer la chaîne WKT avec la longitude et la latitude
    const point: Point<coordinates, coordinates> = {
      type: 'POINT',
      coordinates: [value.lat, value.lng],
    };

    methods.setValue('geom_point', point);
  };

  type DateRange = [Date, Date];

  const handleDateUpdate = (value: DateRange | null) => {
    const start = value === null ? new Date(0) : value[0];
    const end = value === null ? new Date(0) : value[1];
    if (!value || !value[0] || !value[1]) {
      // If a user has cleared or not selected a range
      methods.setMultipleValues({
        start_assignement_date: '',
        end_assignement_date: '',
      });
      return;
    }
    methods.setMultipleValues({
      start_assignement_date: formatDateToLocalString(start),
      end_assignement_date: formatDateToLocalString(end),
    });
  };

  const handleDeviceSelection = (id: number | 0) => {
    const selectedDevice = getEntityById(id, devices);
    methods.setMultipleValues({
      device_id: id,
      device_configuration_id: selectedDevice?.device_configuration_id,
      device_site: {
        ...methods.watch('device_site'),
        device_id: id,
        site_id: site.id,
        device_configuration_id:
          selectedDevice?.device_configuration_id ?? undefined,
      },
    });
  };

  return (
    <div className="customized-dialog-wrapper">
      <CustomizedDialog
        title={t('Créer une nouvelle station de mesure')}
        onCancel={onCancel}
        open={isOpen}
      >
        <form onSubmit={methods.onSubmit} className="form">
          <Stack spacing={2}>
            <DropDownItemSelector
              onSelect={(value) => {
                updateMeasures(value ?? null);
                methods.setValue('device_type_id', value);
              }}
              values={trueDeviceTypes}
              label={t('Type de dispositif')}
              disabled={false}
              name="device_type_id"
              required
              errors={
                methods.hasSubmitted
                  ? {
                      tag: 'Submited',
                      errors: methods.getErrors('device_type_id'),
                    }
                  : { tag: 'Not submitted' }
              }
            />
            <DropDownItemSelector
              onSelect={(value) => {
                handleDeviceSelection(value ?? 0);
              }}
              values={filtredProposition.devices.map((d) => ({
                id: d.id,
                name: `${d.brand}-${d.sn}`,
              }))}
              label={t('Choisir un dispositif existant')}
              disabled={false}
              name="device_id"
              required
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('device_id') }
                  : { tag: 'Not submitted' }
              }
            />
            {filtredProposition.deviceType?.type === 'automatic_sensor' && (
              <DateTimeRangePickerComponent
                format="DD/MM/YYYY"
                onChange={(value) => {
                  handleDateUpdate(value);
                }}
                required
                error={methods.getErrors('end_assignement_date').length !== 0}
                helperText={
                  methods.getErrors('end_assignement_date').length !== 0
                    ? methods.getErrors('end_assignement_date').join('. ')
                    : ''
                }
              />
            )}
            <TextInput
              name="name"
              onChange={(value) => methods.setValue('name', value)}
              label={t('Nom de la station')}
              placeholder={t('Entrer un nom...')}
              required
              errors={
                methods.hasSubmitted
                  ? {
                      tag: 'Submited',
                      errors: methods.getErrors('name'),
                    }
                  : { tag: 'Not submitted' }
              }
            />
            <CoordianatesPicker
              label={t('Position de la station')}
              onClick={(value) => handleCoordinateSelect(value)}
              context={t('de la station')}
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('geom_point') }
                  : { tag: 'Not submitted' }
              }
              required
              defaultValues={{
                position: {
                  lat: defaultValues.geom_point.coordinates[0],
                  lng: defaultValues.geom_point.coordinates[1],
                },
                addressData: site.address
                  ? {
                      road: site.address,
                      city: site.city,
                      postcode: site.postal_code,
                    }
                  : undefined,
              }}
            />
            {filtredProposition.measureTypes.length > 0 && (
              <div
                className="uk-margin"
                style={{
                  backgroundColor: '#00000008',
                  borderRadius: '5px',
                  padding: '15px',
                }}
              >
                <div>Types de mesures</div>
                <div
                  className="uk-margin"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  {filtredProposition.measureTypes.map((mt) => (
                    <div key={mt.id} style={{ display: 'flex', gap: '10px' }}>
                      <span
                        style={{
                          backgroundColor: '#d3f7b0',
                          padding: '5px',
                          borderRadius: '5px',
                        }}
                      >
                        {mt.name}
                      </span>
                      <span
                        style={{
                          backgroundColor: '#f7b0ba',
                          padding: '5px',
                          borderRadius: '5px',
                        }}
                      >
                        {mt.unite}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button
              variant="outlined"
              type="submit"
              className="form-button-submit"
              loadingPosition="end"
              endIcon={<SaveIcon />}
              disabled={state === 'Loading'}
            >
              {state === 'Loading' ? 'Création...' : 'Créer'}
            </Button>
          </Stack>
        </form>
      </CustomizedDialog>
    </div>
  );
}

export default forwardRef(CreateStation);
